import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FilterChip } from '../components/FilterChip';
import { MediaPicker } from '../components/MediaPicker';
import { PageHeader } from '../components/PageHeader';
import { PostGate } from '../components/PostGate';
import { Button } from '../components/ui/Button';
import { Card, SectionTitle } from '../components/ui/Card';
import { connectedServices } from '../data/settings';
import { categories } from '../data/topics';
import { supportsCrossPost } from '../utils/integrations';
import { useLocale } from '../contexts/LocaleContext';
import { useProgress } from '../contexts/ProgressContext';
import { useSocial } from '../contexts/SocialContext';
import type { SportCategory } from '../types';
const postCategories = categories.filter(item => item !== 'all');
const sportCategories: SportCategory[] = ['workout', 'running', 'cycling', 'swimming', 'crossfit', 'strength'];

/** Providers planned to receive posts. None is connected yet. */
const shareTargets = connectedServices.filter(supportsCrossPost);
export function CreatePost() {
  const {
    t,
    tl
  } = useLocale();
  const navigate = useNavigate();
  const progress = useProgress();
  const social = useSocial();
  const [media, setMedia] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [category, setCategory] = useState<SportCategory>('running');
  const [activity, setActivity] = useState('');
  const [location, setLocation] = useState('');
  if (!progress.postUnlocked) {
    return <div className="px-2 pb-2" data-id="element-2566" data-mp="34s8pex0hnx0d">
        <PageHeader title={t('create.post.title')} subtitle={t('create.post.body')} showBack data-id="element-2567" data-mp="2quttd9fqcqzf" />
        <PostGate data-id="element-2568" data-mp="2uvvk11vkzups" />
      </div>;
  }
  const ready = Boolean(media) && caption.trim().length > 0;
  const publish = () => {
    if (!media || !ready) return;
    social.publishPost({
      media,
      caption: caption.trim(),
      category,
      activity: activity.trim() || undefined,
      location: location.trim() || undefined
    });
    progress.consumePostUnlock();
    navigate('/publicado/post', {
      replace: true
    });
  };
  return <div className="px-2 pb-2" data-id="element-2569" data-mp="3h5vl835oishy">
      <PageHeader title={t('create.post.title')} subtitle={progress.postUnlockSource === 'firstDay' ? t('create.post.firstDay') : t('create.post.unlocked')} showBack data-id="element-2570" data-mp="3h6wxuc2cfn1s" />

      <Card className="p-2" data-id="element-2571" data-mp="33dfpzoj0ryrl">
        <SectionTitle data-id="element-2572" data-mp="33eqtrocgt6ip">{t('create.media.title')}</SectionTitle>
        <MediaPicker value={media} onChange={setMedia} data-id="element-2573" data-mp="2ydjjnimunv8v" />
      </Card>

      <Card className="mt-2 p-2" data-id="element-2574" data-mp="2tezyvljzspi3">
        <SectionTitle data-id="element-2575" data-mp="2te6qkg5dxrxu">{t('create.caption.title')}</SectionTitle>
        <textarea value={caption} onChange={event => setCaption(event.target.value)} rows={3} placeholder={t('create.caption.hint')} className="w-full resize-none rounded-card border border-sand-line bg-white/80 p-2 text-[13.5px] leading-relaxed text-deep placeholder:text-muted focus:border-deep/40" data-id="element-2576" data-mp="2ofwsnxsa0d8j" />
      </Card>

      <Card className="mt-2 p-2" data-id="element-2577" data-mp="v5anqwmm3nx7">
        <SectionTitle data-id="element-2578" data-mp="z6w6uzpqoyfu">{t('create.category.title')}</SectionTitle>
        <ul className="bee-rail -mx-2 flex gap-1 overflow-x-auto px-2 pb-1" data-id="element-2579" data-mp="z51itcj68q7j">
          {postCategories.map(item => <li key={item} className="shrink-0" data-id="element-2580" data-mp="18wxszlyj86s4">
              <FilterChip label={t(`category.${item}`)} active={category === item} onClick={() => setCategory(item)} data-id="element-2581" data-mp="18vmfuvj75moc" />
            </li>)}
        </ul>

        {sportCategories.includes(category) && <label className="mt-2 block" data-id="element-2582" data-mp="l6tnta5p3yz0">
            <span className="bee-overline text-[9px] font-bold text-muted" data-id="element-2583" data-mp="l61ts3rqy9au">
              {t('create.activity.title')}
            </span>
            <input value={activity} onChange={event => setActivity(event.target.value)} placeholder={t('create.activity.hint')} className="mt-1 h-11 w-full rounded-card border border-sand-line bg-white/80 px-2 text-[13.5px] text-deep placeholder:text-muted focus:border-deep/40" data-id="element-2584" data-mp="1wms5u3iupvcd" />
          </label>}

        <label className="mt-2 block" data-id="element-2585" data-mp="35ljgzywyxks3">
          <span className="bee-overline text-[9px] font-bold text-muted" data-id="element-2586" data-mp="3jjhwj7n0zn96">
            {t('create.location.title')}
          </span>
          <input value={location} onChange={event => setLocation(event.target.value)} placeholder={t('create.location.hint')} className="mt-1 h-11 w-full rounded-card border border-sand-line bg-white/80 px-2 text-[13.5px] text-deep placeholder:text-muted focus:border-deep/40" data-id="element-2587" data-mp="3eilii18ymkq1" />
        </label>
      </Card>

      {/* Future cross-posting: opt-in per post, never on by default. */}
      <Card className="mt-2 p-2" data-id="element-2588" data-mp="g2ix9qb0jsa4">
        <SectionTitle data-id="element-2589" data-mp="g3kk0s1e637o">{t('share.title')}</SectionTitle>
        <p className="-mt-1 mb-1 text-[11px] leading-snug text-muted" data-id="element-2590" data-mp="g06exc1cffwj">{t('share.body')}</p>
        <ul data-id="element-2591" data-mp="b1brfwsfl363">
          <li className="flex min-h-[48px] items-center justify-between gap-2 border-b border-sand-line" data-id="element-2592" data-mp="up84kebib9r2">
            <span className="text-[13px] font-medium text-deep" data-id="element-2593" data-mp="zo19hicq9sfm">BEELYV</span>
            <span className="bee-overline text-[8.5px] font-bold text-gold-deep" data-id="element-2594" data-mp="zlyl12r847d0">
              {t('share.always')}
            </span>
          </li>
          {shareTargets.map(service => <li key={service.id} className="flex min-h-[48px] items-center justify-between gap-2 border-b border-sand-line last:border-b-0" data-id="element-2595" data-mp="ps7xq16u22yw">
              <span className="text-[13px] font-medium text-muted" data-id="element-2596" data-mp="gr04hxj6bsbo">
                {service.label ? tl(service.label) : service.name}
              </span>
              <span className="bee-overline shrink-0 rounded-pill border border-sand-line px-2 py-[4px] text-[8.5px] font-bold text-muted" data-id="element-2597" data-mp="6z43oeppdo3r">
                {t('services.comingSoon')}
              </span>
            </li>)}
        </ul>
      </Card>

      <Button variant="gold" size="lg" fullWidth className="mt-3" disabled={!ready} onClick={publish} data-id="element-2598" data-mp="3t8y8etdd146x">
        {t('create.publishPost')}
      </Button>
      {!ready && <p className="mt-1 px-1 text-[11px] text-muted" data-id="element-2599" data-mp="1py1zmy7jwjt3">{t('create.needed')}</p>}
    </div>;
}